import '@fortawesome/fontawesome-free/css/all.css';
import '@elotech/arc/src/assets/less/compiled/protocolo.css';

import './icons/IconsLibrary';

import { ErrorPage, KeycloakService } from '@elotech/components';
import {
  buildEntidadeSegment,
  buildRouterBasename,
  changeEntidade,
  extractEntidadeFromPath,
  getBaseHrefCookie,
  isEntidadeIdNumerico,
  isEntidadeNaoResolvida,
  resolveBaseHref,
  setBaseHrefEntidadeCookie
} from 'common/utils';
import { StorageService } from 'libs';
import ReactDOM from 'react-dom';
import {
  EntidadeService,
  ProcessoExternoService,
  ProtocoloConfigService
} from 'service';
import { configureIntl, getEntidadeByPathname, isAuthenticated } from 'utils';

import App from './App';

configureIntl();

const container = document.getElementById('root');
const configStorage = StorageService.getItem('config');
const configExternaStorage = StorageService.getItem('configExterna');
const entidadePathName = getEntidadeByPathname();

// Sinal síncrono e sem rede: URL já tem o segmento BaseHref, ou o cookie
// (setado em um login anterior, inclusive por outro módulo via SSO)
// já resolve uma entidade real. Não usa KeycloakService.getInstance().authenticated
// puro porque esse flag só fica pronto depois de um round-trip assíncrono
// no mount da árvore React — sempre falso na hora deste check, mesmo logo
// após o redirect de login do Keycloak.
const temEntidadeJaResolvida = () => {
  const daUrl = extractEntidadeFromPath();

  if (daUrl && !isEntidadeNaoResolvida(daUrl.idEntidade)) {
    return true;
  }

  const doCookie = getBaseHrefCookie()?.entidade;

  return (
    isEntidadeIdNumerico(doCookie) &&
    !isEntidadeNaoResolvida(doCookie.toString())
  );
};

const isAuthenticatedHint = temEntidadeJaResolvida() || isAuthenticated();

const renderApp = (basename, config, configExterna) => {
  ReactDOM.render(
    <App basename={basename} config={config} configExterna={configExterna} />,
    container
  );
};

const renderError = () => {
  ReactDOM.render(
    <ErrorPage message="Falha ao buscar as configurações iniciais da aplicação." />,
    container
  );
};

const getConfigs = async (basename, entidadeId) => {
  const [config, configExterna] = await Promise.all([
    ProtocoloConfigService.getConfig(entidadeId),
    ProcessoExternoService.getConfig(entidadeId)
  ]);

  StorageService.saveItemAsJson('config', config.data);
  StorageService.saveItemAsJson('configExterna', configExterna.data);

  renderApp(basename, config.data, configExterna.data);
};

const renderComCache = (basename, entidadeId) => {
  const isConfigCacheada =
    configStorage &&
    configExternaStorage &&
    configStorage.idEntidade?.toString() === entidadeId?.toString();

  if (isConfigCacheada) {
    renderApp(basename, configStorage, configExternaStorage);
  } else {
    getConfigs(basename, entidadeId).catch(renderError);
  }
};

// Link antigo tipo /1/consultaProcesso (getEntidadeByPathname) ainda circula.
// Sem isso o resolveBaseHref não reconhece o /1 e resolve por cookie/principal.
const migrarEntidadeLegadaNaUrl = () => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const pathname = window.location.pathname;
  const relativePath = pathname.startsWith(publicUrl)
    ? pathname.slice(publicUrl.length)
    : pathname;
  const resto = relativePath.split('/').slice(2).join('/');
  const agora = new Date();
  const novoSegmento = buildEntidadeSegment({
    idEntidade: String(entidadePathName),
    mes: agora.getMonth() + 1,
    ano: agora.getFullYear()
  });

  window.history.replaceState(
    window.history.state,
    '',
    `${publicUrl}/${novoSegmento}${resto ? `/${resto}` : ''}${
      window.location.search
    }${window.location.hash}`
  );
};

const bootstrapAutenticado = async () => {
  if (!extractEntidadeFromPath() && entidadePathName > 0) {
    migrarEntidadeLegadaNaUrl();
  }

  const resolvido = resolveBaseHref();

  if (isEntidadeNaoResolvida(resolvido.idEntidade)) {
    const isCloud = KeycloakService.getInstance().authenticated;
    const response = await EntidadeService.getEntidadePrincipalPorAmbiente(
      isCloud
    ).catch(() => undefined);
    const idResolvido = response?.data?.id;

    if (idResolvido && !isEntidadeNaoResolvida(idResolvido.toString())) {
      setBaseHrefEntidadeCookie(idResolvido);
      changeEntidade(idResolvido);
      return;
    }
  }

  const basename = buildRouterBasename();

  renderComCache(basename, resolvido.idEntidade);
};

const bootstrapAnonimo = () => {
  const basename = buildRouterBasename();

  EntidadeService.findByIdOrEntidadePrincipal(entidadePathName)
    .then(({ data }) => data.id)
    .then(entidadeId => {
      const entidade = entidadePathName || entidadeId;

      renderComCache(basename, entidade);
    })
    .catch(renderError);
};

if (isAuthenticatedHint) {
  bootstrapAutenticado().catch(renderError);
} else {
  bootstrapAnonimo();
}
